import { type MergePledgeItemDTO, type PledgeItemStatusEnum, type Team } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { useUserPledgeCampaignActivitySummaryQuery } from '../data-access';
import { type WCPledgeActivityTeam } from '../models';
import { getWCPledgeTeamFlagUrl } from '../util';

import { useWCPledgeCampaignDetails } from './useWCPledgeCampaignDetails';

type PledgeItem = {
	amount: string;
	bonus: string;
	status: PledgeItemStatusEnum;
	team: WCPledgeActivityTeam;
};

type UseWCPledgeActivitySummaryReturn = {
	isError: boolean;
	isLoading: boolean;
	pledgeActivitySummary: UseWCPledgeActivitySummary;
};

type UseWCPledgeActivitySummary = {
	activePledges: Array<PledgeItem>;
	claimablePledges: Array<PledgeItem>;
	claimedPledges: Array<PledgeItem>;
	totalActivePledgesAmount: string;
	totalClaimablePledgesAmount: string;
	totalClaimedPledgesAmount: string;
	totalUnstakingPledgesAmount: string;
	unstakingPledges: Array<PledgeItem>;
};

export const useWCPledgeActivitySummary = (): UseWCPledgeActivitySummaryReturn => {
	const { campaignId, isPledgeCampaignDetailsLoading } = useWCPledgeCampaignDetails();

	const { data, isError, isLoading } = useUserPledgeCampaignActivitySummaryQuery(campaignId ?? '');

	const enrichTeamWithFlag = useCallback(
		(team: Team): WCPledgeActivityTeam => ({
			...team,
			flagUrl: getWCPledgeTeamFlagUrl(team)
		}),
		[getWCPledgeTeamFlagUrl]
	);

	const enrichPledgeItems = useCallback(
		(pledgeItems: Array<MergePledgeItemDTO>): Array<PledgeItem> =>
			pledgeItems.map(pledgeItem => ({
				...pledgeItem,
				bonus: pledgeItem.bonus ?? '0',
				team: enrichTeamWithFlag(pledgeItem.team)
			})),
		[]
	);

	const enrichedData = useMemo(
		() => ({
			activePledges: enrichPledgeItems(data?.activePledges ?? []),
			claimablePledges: enrichPledgeItems(data?.claimablePledges ?? []),
			claimedPledges: enrichPledgeItems(data?.claimedPledges ?? []),
			totalActivePledgesAmount: data?.totalActivePledgesAmount ?? '0',
			totalClaimablePledgesAmount: data?.totalClaimablePledgesAmount ?? '0',
			totalClaimedPledgesAmount: data?.totalClaimedPledgesAmount ?? '0',
			totalUnstakingPledgesAmount: data?.totalUnstakingPledgesAmount ?? '0',
			unstakingPledges: enrichPledgeItems(data?.unstakingPledges ?? [])
		}),
		[data]
	);

	return {
		isError,
		isLoading: isPledgeCampaignDetailsLoading || isLoading,
		pledgeActivitySummary: enrichedData
	};
};
